import { defineStore } from 'pinia';
import { ref } from 'vue';
import type { AxiosError } from 'axios';
import type {
  ICurrencyBody,
  ICurrencyDetail,
  ICurrencyDetailResponse,
  ICurrencyListResponse,
  ICurrencyUpdateBody,
} from '@/types/currency.types';
import { CurrencyService } from '@/services/currency.services';
import type {
  IGetAllPromotionsResponse,
  IPromotionBody,
  IPromotionDetailResponse,
  IPromotionUpdateBody,
} from '@/types/promotion.types';
import { PromotionService } from '@/services/promotion.services';

/**
 * Store pour gérer les  currencies et leurs actions associées
 */
export const usePromotionStore = defineStore('promotion', () => {
  /**
   * Crée une nouvelle promotion via le service distant.
   *
   * Cette fonction envoie les données d'une promotion au backend pour
   * l'enregistrement dans la base de données. Elle utilise le service
   * `PromotionService` pour effectuer la requête HTTP.
   *
   * @async
   * @function createPromotion
   * @param {IPromotionBody} data - Données de la promotion à créer (titre, type, valeur, règles, produits, catégories, etc.).
   * @returns {Promise<any>} Promesse résolue avec la réponse de l'API contenant les informations de la promotion créée.
   * @throws {AxiosError} Lève une erreur si la requête échoue ou si le service renvoie une erreur.
   */
  const createPromotion = async (data: IPromotionBody): Promise<any> => {
    try {
      const response = await PromotionService.createPromotion(data);
      return response;
    } catch (error) {
      return Promise.reject(error as AxiosError);
    }
  };

  /**
   * État réactif contenant la liste paginée des promotions récupérées depuis l’API.
   *
   * Ce `ref` stocke la réponse typée de l’API après l’appel à `getPromotion`.
   * Il est mis à jour automatiquement lorsque les données sont chargées
   * depuis le service distant.
   *
   * @type {Ref<IGetAllPromotionsResponse | null>}
   */
  const dataToGet = ref<IGetAllPromotionsResponse | null>(null);

  /**
   * Récupère la liste paginée des promotions depuis le service distant.
   *
   * Cette fonction effectue une requête HTTP via `PromotionService`
   * pour récupérer la liste des promotions disponibles,
   * puis met à jour l’état réactif `dataToGet` avec les données renvoyées.
   *
   * @async
   * @function getPromotion
   * @returns {Promise<IGetAllPromotionsResponse>} Promesse résolue contenant les données paginées des promotions récupérées.
   * @throws {AxiosError} Lève une erreur si la récupération échoue.
   */
  const getPromotion = async (): Promise<IGetAllPromotionsResponse> => {
    try {
      const response = await PromotionService.getPromotion();
      dataToGet.value = response.data;
      return response.data;
    } catch (error) {
      return Promise.reject(error as AxiosError);
    }
  };

  /**
   * État réactif contenant les informations d’une promotion récupérée depuis l’API.
   *
   * Ce `ref` stocke la réponse typée de l’API après l’appel à `getPromotionById`.
   * Il est mis à jour automatiquement lorsque les données sont chargées
   * depuis le service distant.
   *
   * @type {Ref<IPromotionDetailResponse | null>}
   */
  const dataToGetById = ref<IPromotionDetailResponse | null>(null);

  /**
   * Récupère une promotion spécifique depuis le service distant par son identifiant.
   *
   * Cette fonction effectue une requête HTTP via `PromotionService`
   * pour obtenir les informations complètes de la promotion, puis met à jour
   * l’état réactif `dataToGetById` avec les données reçues.
   *
   * @async
   * @function getPromotionById
   * @returns {Promise<IPromotionDetailResponse>} Promesse résolue contenant les données détaillées de la promotion récupérée.
   * @throws {AxiosError} Lève une erreur si la récupération échoue.
   */
  const getPromotionById = async (): Promise<IPromotionDetailResponse> => {
    try {
      const response = await PromotionService.getPromotionById();
      dataToGetById.value = response.data;
      return response.data;
    } catch (error) {
      return Promise.reject(error as AxiosError);
    }
  };

  /**
   * Récupère une promotion spécifique depuis le service distant par son identifiant.
   *
   * Cette fonction effectue une requête HTTP via `PromotionService`
   * pour obtenir les informations complètes de la promotion, puis met à jour
   * l’état réactif `dataToGetById` avec les données reçues.
   *
   * @async
   * @function getPromotionById
   * @returns {Promise<IPromotionDetailResponse>} Promesse résolue contenant les données détaillées de la promotion récupérée.
   * @throws {AxiosError} Lève une erreur si la récupération échoue.
   */
  const getPromotionAnalyticsById =
    async (): Promise<IPromotionDetailResponse> => {
      try {
        const response = await PromotionService.getPromotionAnalyticsById();
        dataToGetById.value = response.data;
        return response.data;
      } catch (error) {
        return Promise.reject(error as AxiosError);
      }
    };

  /**
   * Met à jour une promotion spécifique sur le service distant.
   *
   * Cette fonction effectue une requête HTTP via `PromotionService`
   * pour modifier les informations d’une promotion existante,
   * puis renvoie les données mises à jour.
   *
   * @async
   * @function updatePromotion
   * @param {Partial<IPromotionUpdateBody>} updateData - Données partielles de la promotion à mettre à jour.
   * @returns {Promise<IPromotionDetailResponse>} Promesse résolue contenant les données mises à jour de la promotion.
   * @throws {AxiosError} Lève une erreur si la mise à jour échoue.
   */
  const updatePromotion = async (
    updateData: Partial<IPromotionUpdateBody>,
  ): Promise<IPromotionDetailResponse> => {
    try {
      const response = await PromotionService.updatePromotion(updateData);
      return response.data;
    } catch (error) {
      return Promise.reject(error as AxiosError);
    }
  };

  /**
   * Supprime une promotion spécifique via le service distant.
   *
   * Cette fonction envoie une requête HTTP à l’aide de `PromotionService`
   * pour retirer une promotion existante du système,
   * puis renvoie la réponse du serveur en cas de succès.
   *
   * @async
   * @function deletePromotion
   * @returns {Promise<any>} Promesse résolue contenant la réponse de l’API après suppression.
   * @throws {AxiosError} Lève une erreur si la suppression échoue.
   */
  const deletePromotion = async (): Promise<any> => {
    try {
      const response = await PromotionService.deletePromotion();
      return response;
    } catch (error) {
      return Promise.reject(error as AxiosError);
    }
  };

  return {
    createPromotion,
    getPromotion,
    dataToGet,
    getPromotionById,
    dataToGetById,
    getPromotionAnalyticsById,
    updatePromotion,
    deletePromotion,
  };
});
